import type { FC } from "react";

import { Badge } from "@mantine/core";

import type { EvaluationDetailsBody } from "../api/evaluations/types";

export interface EvaluationCalloutBadgeProps {
    activeBehavior: EvaluationDetailsBody;
    transcriptLength: number;
}

/**
 * Pass/fail pill for a single evaluated behavior. Extracted from `EvaluationCallout` so the ASCEND
 * behaviors list can reuse the exact same content + color logic. Behavior is unchanged:
 * - "Not detected" (red) when the behavior points at the last transcript line and was not present.
 * - "Growth area" when `behaviorPresent === 0`.
 * - "Great job" when `behaviorPresent === 1`.
 */
export const EvaluationCalloutBadge: FC<EvaluationCalloutBadgeProps> = ({ activeBehavior, transcriptLength }) => {
    const behaviorLine = parseInt(activeBehavior.behaviorLine?.[0] ?? "-1");
    const wasBehaviorNotDetected =
        behaviorLine >= 0 && behaviorLine === transcriptLength - 1 && activeBehavior.behaviorPresent !== 1;
    let badgeContent = {
        message: "",
        color: "",
    };

    if (wasBehaviorNotDetected) {
        badgeContent = {
            message: "Not detected",
            color: "var(--mantine-color-red-6)",
        };
    } else if (activeBehavior.behaviorPresent === 0) {
        badgeContent = {
            message: "Growth area",
            color: "var(--mantine-color-primary-3)",
        };
    } else if (activeBehavior.behaviorPresent === 1) {
        badgeContent = {
            message: "Great job",
            color: "var(--mantine-color-success-5)",
        };
    }

    return (
        <Badge variant="outline" color={badgeContent.color} bdrs="5px">
            {badgeContent.message}
        </Badge>
    );
};

export default EvaluationCalloutBadge;
